import { atom, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';
import * as React from 'react';

/**
 * Map of organizationId -> (siteId -> epoch ms of last view).
 */
type LastViewedSites = Record<string, Record<string, number>>;

/**
 * Atom tracking the last time the current user viewed each site, per organization.
 * Persisted to localStorage so it survives reloads and is scoped to the current browser/user.
 */
export const lastViewedSitesAtom = atomWithStorage<LastViewedSites>(
    'lastViewedSitesAtom',
    {},
    undefined,
    { getOnInit: true }
);

/**
 * Selector returning the `siteId -> epoch ms` map for a given organization.
 */
export const lastViewedSitesSelector = atomFamily((orgId: string) =>
    atom((get) => get(lastViewedSitesAtom)[orgId] ?? {})
);

/**
 * Record the current timestamp as the last time the user viewed the given site.
 * Intended to be called once per site screen mount.
 */
export function useMarkSiteAsLastViewed(input: { siteId: string; organizationId: string }) {
    const { siteId, organizationId } = input;
    const setLastViewedSites = useSetAtom(lastViewedSitesAtom);

    React.useEffect(() => {
        setLastViewedSites((prev) => ({
            ...prev,
            [organizationId]: {
                ...(prev[organizationId] ?? {}),
                [siteId]: Date.now(),
            },
        }));
    }, [siteId, organizationId, setLastViewedSites]);
}
